import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSettings, trackHeroEvent } from '../../services/api';
import { Aperture, Phone, Film, Mic, Volume2, VolumeX } from 'lucide-react';
import GalleryOrbitOverlay from './GalleryOrbitOverlay';
import EmberField from './EmberField';
import IrisTransition from './IrisTransition';
import HeadAssembly from './HeadAssembly';
import GalaxyDisc from './GalaxyDisc';
import ShootingStars from './ShootingStars';
import { playShutter } from '../../utils/shutterSound';
import '../../styles/experience.css';

const NAV_ITEMS = [
  { id: 'about', label: 'About', head: 'upleft', video: '/experience/djimic_popup.mp4', pos: 'left', nx: -0.6, ny: -0.8, Icon: Mic },
  { id: 'gallery', label: 'Gallery', head: 'left', video: '/experience/canon_popup.mp4', pos: 'left', nx: -1, ny: 0, Icon: Aperture },
  { id: 'motion', label: 'In Motion', head: 'upright', video: '/experience/osmo_popup.mp4', pos: 'right', nx: 0.6, ny: -0.8, Icon: Film },
  { id: 'contact', label: 'Contact', head: 'right', video: '/experience/phone_popup.mp4', pos: 'right', nx: 1, ny: 0, Icon: Phone },
];

const HEADS = ['center', 'left', 'upleft', 'upright', 'right'];

const InteractiveHero = () => {
  const [focus, setFocus] = useState(null);
  const [galleryMode, setGalleryMode] = useState(false);
  const [iris, setIris] = useState(null);
  const [info, setInfo] = useState(null);
  const [brokenVideos, setBrokenVideos] = useState({});
  const [muted, setMuted] = useState(() => localStorage.getItem('xp-muted') === '1');
  const [intro, setIntro] = useState(
    () => !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
  const stageRef = useRef(null);
  const hoverTracked = useRef(new Set());
  const navigate = useNavigate();

  // Replay the ember-assembly intro every 15-20s while the hero is idle
  useEffect(() => {
    if (intro || galleryMode || focus || iris) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const t = setTimeout(() => setIntro(true), 15000 + Math.random() * 5000);
    return () => clearTimeout(t);
  }, [intro, galleryMode, focus, iris]);

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    localStorage.setItem('xp-muted', next ? '1' : '0');
  };

  useEffect(() => {
    getSettings()
      .then((s) => setInfo({ name: s.photographer_name, tagline: s.tagline }))
      .catch(() => setInfo({ name: 'Portfolio', tagline: 'Photographer' }));
  }, []);

  useEffect(() => {
    // Warm the browser cache for popup clips on desktop
    if (window.innerWidth > 768) {
      NAV_ITEMS.forEach((n) => {
        const v = document.createElement('video');
        v.preload = 'auto';
        v.muted = true;
        v.src = n.video;
      });
    }
  }, []);

  const handleMouseMove = useCallback((e) => {
    const stage = stageRef.current;
    if (!stage) return;
    const rect = stage.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    stage.style.setProperty('--mx', x.toFixed(3));
    stage.style.setProperty('--my', y.toFixed(3));
  }, []);

  const departWithIris = (hash) => {
    setIris('closing');
    setTimeout(() => {
      sessionStorage.setItem('xp-arrive-iris', '1');
      navigate(`/portfolio#${hash}`);
    }, 520);
  };

  const handleNavClick = (id) => {
    trackHeroEvent(id, 'click');
    setFocus(id);
    if (!muted) playShutter();
    window.dispatchEvent(new Event('xp-burst'));
    if (id === 'gallery') {
      setGalleryMode(true);
    } else {
      departWithIris(id);
    }
  };

  const handleNavEnter = (id) => {
    setFocus(id);
    if (!muted) playShutter();
    if (!hoverTracked.current.has(id)) {
      hoverTracked.current.add(id);
      trackHeroEvent(id, 'hover');
    }
  };

  const handleCategorySelect = (categoryId) => {
    sessionStorage.setItem('xp-filter', categoryId);
    departWithIris('gallery');
  };

  const activeHead = focus ? NAV_ITEMS.find((n) => n.id === focus)?.head : 'center';

  return (
    <section className="xp-hero" id="home" onMouseMove={handleMouseMove} data-testid="interactive-hero">
      <GalaxyDisc excited={intro || !!focus} />
      <ShootingStars />
      <EmberField focus={focus} />
      <a
        href="#home"
        className="xp-logo"
        data-testid="xp-logo"
        aria-label="Kelvin the Kreator — Home"
        onClick={(e) => {
          e.preventDefault();
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      >
        <img
          src="https://customer-assets.emergentagent.com/job_picturegallery-1/artifacts/ds0tfjjj_Kelvin%20the%20Kreator%20Logo.png"
          alt="Kelvin the Kreator logo"
          draggable={false}
        />
      </a>
      <div className="xp-stage" ref={stageRef}>
        <div className="xp-ring xp-ring-inner" aria-hidden="true"></div>
        <div className="xp-ring xp-ring-outer" aria-hidden="true"></div>

        <div className={`xp-head ${focus ? 'eroding' : ''}`} data-testid="xp-head">
          {HEADS.map((h) => (
            <img
              key={h}
              src={`/experience/head_${h}.webp`}
              alt=""
              className={activeHead === h ? 'active' : ''}
              draggable={false}
            />
          ))}
        </div>

        <nav
          className="xp-nav"
          aria-label="Interactive navigation"
          style={{ opacity: intro ? 0 : 1, pointerEvents: intro ? 'none' : 'auto', transition: 'opacity 0.6s ease' }}
        >
          {NAV_ITEMS.map((item) => (
            <div
              key={item.id}
              className={`xp-nav-item ${focus === item.id ? 'focused' : ''}`}
              style={{ '--nx': item.nx, '--ny': item.ny }}
              onMouseEnter={() => handleNavEnter(item.id)}
              onMouseLeave={() => setFocus(null)}
            >
              <button
                className="xp-nav-btn"
                onClick={() => handleNavClick(item.id)}
                data-testid={`xp-nav-${item.id}`}
              >
                <span className="xp-nav-dot"></span>
                {item.label}
              </button>

              {focus === item.id && (
                <div className={`xp-popup xp-popup-${item.pos}`} data-testid={`xp-popup-${item.id}`}>
                  {brokenVideos[item.id] ? (
                    <div className="xp-popup-fallback">
                      <item.Icon size={64} strokeWidth={1} />
                    </div>
                  ) : (
                    <video
                      src={item.video}
                      autoPlay
                      muted
                      loop
                      playsInline
                      onError={() => setBrokenVideos((b) => ({ ...b, [item.id]: true }))}
                    />
                  )}
                </div>
              )}
            </div>
          ))}
        </nav>
      </div>

      {intro ? (
        <HeadAssembly
          onDone={() => {
            setIntro(false);
          }}
        />
      ) : (
        <div className="xp-identity" data-testid="xp-identity">
        <h1 className="xp-name" aria-label={info?.name || ''}>
          {(info?.name || '').split('').map((ch, i) => (
            <span key={i} className="xp-letter" style={{ '--li': i }}>
              {ch === ' ' ? '\u00A0' : ch}
            </span>
          ))}
        </h1>
        <p className="xp-tagline">{info?.tagline || ''}</p>
      </div>
      )}
      <button
        className="xp-sound-toggle"
        onClick={toggleMute}
        data-testid="xp-sound-toggle"
        aria-label={muted ? 'Unmute sound effects' : 'Mute sound effects'}
      >
        {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
      </button>

      {galleryMode && (
        <GalleryOrbitOverlay
          onClose={() => setGalleryMode(false)}
          onSelect={handleCategorySelect}
        />
      )}

      <IrisTransition phase={iris} />
    </section>
  );
};

export default InteractiveHero;
